import React, { useState, useRef } from 'react';
import { CircleCheck as CheckCircle, User, MapPin, Users, FileText, Camera, Loader, CircleAlert as AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { RegistrationData, RegistrationLink } from './types';
import { formatCurrency, DOCUMENT_CATEGORIES } from './types';

interface Props {
  data: RegistrationData;
  link: RegistrationLink;
  registrationId: string;
  onComplete: () => void;
}

export default function ConfirmationStep({ data, link, registrationId, onComplete }: Props) {
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittingLock = useRef(false);

  const handleFinish = async () => {
    if (!confirmed) return;
    if (submittingLock.current) return;
    submittingLock.current = true;
    setSubmitting(true);
    setError(null);

    try {
      const residentialAddress = `${data.enderecoResidencial.logradouro}, ${data.enderecoResidencial.numero}${data.enderecoResidencial.complemento ? `, ${data.enderecoResidencial.complemento}` : ''} - ${data.enderecoResidencial.bairro}, ${data.enderecoResidencial.cidade}/${data.enderecoResidencial.estado} - CEP: ${data.enderecoResidencial.cep}`;
      const commercialAddress = `${data.enderecoComercial.logradouro}, ${data.enderecoComercial.numero}${data.enderecoComercial.complemento ? `, ${data.enderecoComercial.complemento}` : ''} - ${data.enderecoComercial.bairro}, ${data.enderecoComercial.cidade}/${data.enderecoComercial.estado} - CEP: ${data.enderecoComercial.cep}`;

      // 1. Create contract — goes to vendor_review first (seller must approve before manager sees it)
      const { data: contract, error: contractError } = await supabase
        .from('contracts')
        .insert({
          client_name: data.name,
          cpf: data.cpf,
          phone: data.phone,
          relative_phones: data.contacts.map(c => ({ numero: c.telefone, relacao: c.relacao })),
          address: residentialAddress,
          business_address: commercialAddress,
          pix_key: data.pixKey,
          bank: data.bank,
          service_entry_time: data.serviceEntryTime,
          service_exit_time: data.serviceExitTime,
          residential_complement: data.enderecoResidencial.complemento,
          commercial_complement: data.enderecoComercial.complemento,
          seller_id: link.seller_id,
          unit_id: link.unit_id,
          loan_amount: link.loan_amount,
          installments: link.installments,
          interest_rate: link.interest_rate,
          total_amount: link.total_amount,
          ruta: link.ruta,
          approval_status: 'vendor_review',
        })
        .select()
        .single();

      if (contractError) throw contractError;

      // 2. Create installments — same structure as LoanSimulation.tsx
      const installmentInserts = link.installment_dates.map((date) => ({
        contract_id: contract.id,
        unit_id: link.unit_id,
        due_date: date,
        amount: link.installment_amount,
        status: 'pending',
      }));

      const { error: installmentsError } = await supabase
        .from('installments')
        .insert(installmentInserts);

      if (installmentsError) throw installmentsError;

      // 3. Copy all uploaded documents to contract_files table — same structure as LoanSimulation.tsx
      const { data: regFiles } = await supabase
        .from('self_registration_files')
        .select('*')
        .eq('registration_id', registrationId);

      if (regFiles && regFiles.length > 0) {
        const contractFileInserts = regFiles.map((f: any) => ({
          contract_id: contract.id,
          unit_id: link.unit_id,
          file_name: f.file_name,
          file_type: f.file_type,
          file_url: f.file_url,
        }));

        await supabase.from('contract_files').insert(contractFileInserts);
      }

      // Also add selfie if available
      if (data.selfieUrl) {
        await supabase.from('contract_files').insert({
          contract_id: contract.id,
          unit_id: link.unit_id,
          file_name: 'selfie-inicial.jpg',
          file_type: 'image/jpeg',
          file_url: data.selfieUrl,
        });
      }

      const now = new Date().toISOString();

      // 4. Update self_registration record
      await supabase
        .from('client_self_registrations')
        .update({
          status: 'completed',
          completed_at: now,
          contract_id: contract.id,
          declaration_confirmed: true,
        })
        .eq('id', registrationId);

      // 5. Update link: completed, inactive
      await supabase
        .from('seller_registration_links')
        .update({
          status: 'completed',
          completed_at: now,
          is_active: false,
        })
        .eq('id', link.id);

      onComplete();
    } catch (err) {
      console.error('Error finalizing registration:', err);
      setError('Erro ao finalizar o cadastro. Por favor, tente novamente.');
    } finally {
      setSubmitting(false);
      submittingLock.current = false;
    }
  };

  const Section = ({ icon: Icon, title, children }: { icon: React.ElementType; title: string; children: React.ReactNode }) => (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
        <Icon className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-semibold text-gray-700">{title}</span>
      </div>
      <div className="p-4 space-y-1.5">{children}</div>
    </div>
  );

  const Row = ({ label, value }: { label: string; value: string }) => (
    <div className="flex justify-between gap-2">
      <span className="text-sm text-gray-500 flex-shrink-0">{label}</span>
      <span className="text-sm font-medium text-gray-900 text-right">{value || '—'}</span>
    </div>
  );

  const completedDocs = data.documents.filter(d => d.url !== null).length;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Revisar e Finalizar</h2>
        <p className="text-gray-600 text-sm">
          Verifique todas as informações antes de concluir o cadastro.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Selfie preview */}
      {data.selfieUrl && (
        <Section icon={Camera} title="Selfie capturada">
          <div className="flex items-center gap-3">
            <img
              src={data.selfieUrl}
              alt="Selfie"
              className="w-16 h-16 rounded-xl object-cover border border-gray-200"
            />
            <div>
              <p className="text-sm font-medium text-emerald-700 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Selfie capturada
              </p>
              {data.latitude && (
                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {data.latitude.toFixed(5)}, {data.longitude?.toFixed(5)}
                </p>
              )}
            </div>
          </div>
        </Section>
      )}

      {/* Loan data */}
      <Section icon={FileText} title="Dados do Empréstimo">
        <Row label="Valor" value={formatCurrency(link.loan_amount)} />
        <Row label="Parcelas" value={`${link.installments}x ${formatCurrency(link.installment_amount)}`} />
        <Row label="Total a pagar" value={formatCurrency(link.total_amount)} />
        <Row label="Vendedora" value={link.seller_name} />
      </Section>

      {/* Personal data */}
      <Section icon={User} title="Dados Pessoais">
        <Row label="Nome" value={data.name} />
        <Row label="CPF" value={data.cpf} />
        <Row label="Telefone" value={data.phone} />
        <Row label="End. residencial" value={data.enderecoResidencial.logradouro ? `${data.enderecoResidencial.logradouro}, ${data.enderecoResidencial.numero}` : ''} />
        <Row label="End. comercial" value={data.enderecoComercial.logradouro ? `${data.enderecoComercial.logradouro}, ${data.enderecoComercial.numero}` : ''} />
        <Row label="PIX" value={data.pixKey} />
        <Row label="Banco" value={data.bank} />
      </Section>

      {/* Contacts */}
      <Section icon={Users} title={`Contatos (${data.contacts.length})`}>
        {data.contacts.map((c, i) => (
          <div key={i} className="flex items-center gap-2 py-1 border-b last:border-0 border-gray-50">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{c.nome}</p>
              <p className="text-xs text-gray-500">{c.telefone} · {c.relacao}</p>
            </div>
          </div>
        ))}
      </Section>

      {/* Documents */}
      <Section icon={FileText} title={`Documentos (${completedDocs}/${DOCUMENT_CATEGORIES.length})`}>
        {data.documents.map((doc) => (
          <div key={doc.category} className="flex items-center gap-2">
            {doc.url
              ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
              : <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />}
            <span className="text-xs text-gray-600 truncate">{doc.label}</span>
          </div>
        ))}
      </Section>

      {/* Declaration */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 w-5 h-5 rounded border-gray-300 text-blue-600 cursor-pointer flex-shrink-0"
          />
          <span className="text-sm text-blue-900 leading-relaxed">
            <strong>Declaro que todas as informações enviadas são verdadeiras</strong> e autorizo a análise dos dados e documentos informados para fins de análise de crédito.
          </span>
        </label>
      </div>

      <button
        onClick={handleFinish}
        disabled={!confirmed || submitting}
        className="w-full py-4 bg-emerald-600 text-white rounded-xl font-semibold text-lg hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {submitting ? (
          <><Loader className="w-5 h-5 animate-spin" /> Finalizando...</>
        ) : (
          <><CheckCircle className="w-5 h-5" /> Finalizar Cadastro</>
        )}
      </button>
    </div>
  );
}
